/* eslint-disable @typescript-eslint/no-unused-vars */
import { call, delay, put, select, takeLatest } from 'redux-saga/effects'

import { createSagas } from 'src/helpers/redux'
import * as storageObjectsHelpers from 'src/helpers/storageObjects'
import store from 'src/store'
import { IResponse } from 'src/services/storage/project/http'
import * as schemaService from 'src/services/storage/project/scheme'
import * as smartListService from 'src/services/storage/project/scheme/smart-list'
import * as stageSortService from 'src/services/storage/project/scheme/objects/stage-sort'
import { realtimeService } from 'src/services/realtime'

import * as appStore from 'src/store/modules/app'
import { IStateData as AppStoreState } from 'src/store/modules/app/state'
import { IModuleConfigStrict } from './index'
import * as state from './state'
import { Actions, createFullfiledAction } from './actions'
import { Setters } from './index'
import { Selectors } from './selectors'

import * as app from 'src/store/modules/app'
import { IStorageFilterRule } from 'src/modals/storageFilters/components/FilterField'

function normalizeFilterRules(
  conditions: IStorageFilterRule[]
): Omit<IStorageFilterRule, 'additionalParams'>[] {
  return Array.isArray(conditions)
    ? conditions?.map(({ additionalParams, ...systemValues }) => {
        if (systemValues.conditions) {
          return {
            ...systemValues,
            conditions: normalizeFilterRules(systemValues.conditions),
          }
        }

        return systemValues
      })
    : conditions
}

export default (
  config: IModuleConfigStrict,
  actions: Actions,
  setters: Setters,
  selectors: Selectors
) => {
  // Helper function to subscribe to smart list recalculation events
  // Uses consistent key format to prevent duplicate subscriptions
  const subscribeToSmartListRecalculation = () => {
    const key = `smartList`

    realtimeService.only<smartListService.IGetSmartListItemData>(
      {
        key,
        event: 'recalculated',
        channel: 'storage:smart_lists',
      },
      (data) => {
        store.dispatch(setters.updateSmartListItem(data))
        store.dispatch(actions.syncTableColumns.pending())
      }
    )
  }

  return createSagas<Actions>(actions, {
    getMoreObjectList: function* ({ payload }) {
      const receivedObjects: IResponse<schemaService.IGetObjectsData[]> =
        yield call(schemaService.getObjects, {
          schemeName: config.schemeName,
          ...payload,
          filters: {
            ...payload.filters,
            conditions: normalizeFilterRules(payload.filters?.conditions || []),
          },
        })

      yield put(
        setters.mergeData({
          items: receivedObjects.data,
          previousCursor: receivedObjects.meta.prevCursor,
          nextCursor: receivedObjects.meta.nextCursor,
        })
      )
    },
    changeObjectList: function* ({ payload }) {
      yield put(setters.setChangeObjectList(payload))
    },
    deleteObjectItem: {
      pending: function* ({ payload }) {
        const response: IResponse<
          schemaService.IDeleteValidationObjectByIdData[]
        > = yield call(schemaService.deleteObject, payload)

        if (
          payload.action === schemaService.EDeleteObjectAction.VALIDATE_DELETE
        ) {
          const validationResult = Object.values(response.data)

          yield put(
            setters.setObjectDeleteValidation(
              validationResult.length > 0
                ? validationResult
                : [{ type: 'confirmation' }]
            )
          )
        }
      },

      clear: function* () {
        yield put(setters.setObjectDeleteValidation(null))
      },
    },
    updateObjectItem: function* ({ payload }) {
      yield put(setters.updateItem(payload))
    },

    postDeleteAllSchemaObject: {
      pending: function* ({ payload }) {
        yield call(schemaService.postDeleteAllSchemaObjects, payload)

        yield put(
          setters.setList({
            parentObjectUniqId: undefined,
            items: [],
            previousCursor: null,
            nextCursor: null,
            total: undefined,
          })
        )
      },
      clear: function* () {
        yield put(setters.setObjectDeleteValidation(null))
      },
    },

    postDeleteAllSchemaObjectsValidation: function* ({ payload }) {
      const response: IResponse<
        schemaService.IDeleteValidationObjectByIdData[]
      > = yield call(
        schemaService.postDeleteAllSchemaObjectsValidation,
        payload
      )

      yield put(setters.setObjectDeleteValidation(response.data))
    },

    getObjectsList: {
      effect: 'latest',
      pending: function* ({ payload }) {
        if (payload.search) {
          yield delay(2000)
        }

        yield put(actions.watcherListEvents(payload))

        const objectList: IResponse<schemaService.IGetObjectsData[]> =
          yield call(schemaService.getObjects, {
            schemeName: config.schemeName,
            ...payload,
            filters: {
              ...payload.filters,
              conditions: normalizeFilterRules(
                payload.filters?.conditions || []
              ),
            },
          })

        yield put(
          setters.setList({
            parentObjectUniqId: payload.parentObjectUniqId || undefined,
            items: objectList.data,
            previousCursor: objectList.meta.prevCursor,
            nextCursor: objectList.meta.nextCursor,
            total: objectList.meta.total,
          })
        )
      },
      clear: function* () {
        yield put(
          setters.setList({
            items: [],
            parentObjectUniqId: undefined,
            previousCursor: null,
            nextCursor: null,
          })
        )
      },
      rejected: function* () {
        yield put(
          setters.setList({
            items: [],
            parentObjectUniqId: undefined,
            previousCursor: null,
            nextCursor: null,
          })
        )
      },
    },
    watcherListEvents: function* ({ payload }) {
      const listKey = `${
        config.schemeName
      }${actions.watcherListEvents.pending.toString()}`

      const channel = `storage:${config.schemeName}`

      const insertEvent = {
        key: `${listKey}/insert`,
        event: 'insert',
        channel,
      }
      const updateEvent = {
        key: `${listKey}/update`,
        event: 'update',
        channel,
      }
      const deleteEvent = {
        key: `${listKey}/delete`,
        event: 'delete',
        channel,
      }

      if (
        !payload.filters &&
        !payload.search &&
        !payload.smartListId &&
        !payload.sort
      ) {
        realtimeService.only<schemaService.IGetObjectsData>(
          insertEvent,
          (data) => store.dispatch(actions.getObjectAbly(data))
        )

        realtimeService.only<schemaService.IGetObjectsData>(
          updateEvent,
          (data) => {
            store.dispatch(setters.setListItem(data))
          }
        )

        realtimeService.only<schemaService.IGetObjectsData>(
          deleteEvent,
          (data) => store.dispatch(setters.deleteObjectItem([data.uniqId]))
        )
      } else {
        realtimeService.offOnly(insertEvent)
        realtimeService.offOnly(updateEvent)
        realtimeService.offOnly(deleteEvent)
      }
    },

    getObjectAbly: {
      effect: 'every',
      saga: function* ({ payload }) {
        const { uniqId } = payload

        try {
          const objectItem: IResponse<schemaService.IGetObjectsData> =
            yield call(schemaService.getObjectsById, {
              schemeName: config.schemeName,
              uniqId,
            })

          yield put(setters.setListAddFirst(objectItem.data))
        } catch (e) {}
      },
    },

    getObjectsItem: {
      effect: 'latest',
      pending: function* ({ payload }) {
        const { uniqId } = payload

        const objectItem: IResponse<schemaService.IGetObjectsData> = yield call(
          schemaService.getObjectsById,
          {
            schemeName: config.schemeName,
            uniqId,
          }
        )

        yield put(setters.setItem(objectItem.data))
      },
      clear: function* () {
        yield put(setters.setItem(undefined))
      },
    },
    postObjectsItem: function* ({ payload }) {
      const { data, fullfiledAction, fullfiledActionContext } = payload
      const item: IResponse<schemaService.IGetObjectsData> = yield call(
        schemaService.postObjects,
        {
          schemeName: config.schemeName,
          data,
        }
      )

      if (fullfiledAction) {
        if (typeof fullfiledAction === 'string') {
          yield put(
            createFullfiledAction(fullfiledAction, {
              data: item.data,
              context: fullfiledActionContext,
            })
          )
        }
        if (typeof fullfiledAction === 'object' && fullfiledAction.type) {
          yield put(fullfiledAction)
        }
      }
      yield put(
        app.actions.tost.pending({
          type: 'success',
          title: 'Object has been successfully created',
          description: '',
        })
      )
    },

    changeListItem: function* ({ payload }) {
      yield put(setters.setListItem(payload))
    },

    putObjectsItem: {
      effect: 'every',
      saga: function* ({ payload }) {
        const { uniqId, data, fullfiledAction, fullfiledActionContext } =
          payload

        const item: IResponse<state.IListItem> = yield call(
          schemaService.putObjects,
          {
            schemeName: config.schemeName,
            uniqId,
            data,
          }
        )

        yield put(setters.setItem(item.data))

        if (fullfiledAction) {
          if (typeof fullfiledAction === 'string') {
            yield put(
              createFullfiledAction(fullfiledAction, {
                data: item.data,
                context: fullfiledActionContext,
              })
            )
          }
          if (typeof fullfiledAction === 'object' && fullfiledAction.type) {
            yield put(fullfiledAction)
          }
        }
        yield put(
          app.actions.tost.pending({
            type: 'success',
            title: 'Object has been successfully updated',
            description: '',
          })
        )
      },
    },

    getFieldsGroups: function* ({ payload }) {
      const { limit, cursor } = payload

      const fieldsGroups: IResponse<schemaService.IFieldsGroupData[]> =
        yield call(schemaService.getFieldsGroups, {
          schemeName: config.schemeName,
          limit,
          cursor,
        })

      yield put(
        setters.setFieldsGroups({
          items: fieldsGroups.data,
          previousCursor: fieldsGroups.previousCursor,
          nextCursor: fieldsGroups.nextCursor,
        })
      )

      yield put(actions.syncTableColumns.pending())
    },

    setTableColumnsPriority: {
      effect: 'every',
      saga: function* ({ payload }) {
        const { identifier, index } = payload

        yield put(
          setters.setTableColumnsPriority({
            identifier,
            index,
          })
        )
        yield put(actions.saveTableColumns.pending())
      },
    },

    setTableColumnsEnable: {
      effect: 'every',
      saga: function* ({ payload }) {
        const { identifier, enable } = payload

        yield put(
          setters.setTableColumnsEnable({
            identifier,
            enable,
          })
        )
        yield put(actions.saveTableColumns.pending())
      },
    },
    setTableColumnsWidth: {
      effect: 'every',
      saga: function* ({ payload }) {
        const { identifier, width } = payload

        yield put(
          setters.setTableColumnsWidth({
            identifier,
            width,
          })
        )
        yield put(actions.saveTableColumns.pending())
      },
    },

    setBoardStagePriority: {
      effect: 'every',
      saga: function* ({ payload }) {
        yield put(setters.setBoardStagePriority(payload))
        yield put(actions.saveTableColumns.pending())
      },
    },
    setBoardStageEnable: {
      effect: 'every',
      saga: function* ({ payload }) {
        yield put(setters.setBoardStageEnable(payload))
        yield put(actions.saveTableColumns.pending())
      },
    },
    setBoardFieldsPriority: {
      effect: 'every',
      saga: function* ({ payload }) {
        yield put(setters.setBoardFieldsPriority(payload))
        yield put(actions.saveTableColumns.pending())
      },
    },
    setBoardFieldsEnable: {
      effect: 'every',
      saga: function* ({ payload }) {
        yield put(setters.setBoardFieldsEnable(payload))
        yield put(actions.saveTableColumns.pending())
      },
    },
    setBoardCardUpdateFulfiled: function* ({ payload }) {
      yield put(setters.setListItem(payload.data))
    },
    setBoardCardCreateFulfiled: function* ({ payload }) {
      const objectWithPerms: IResponse<schemaService.IGetObjectsData> =
        yield call(schemaService.getObjectsById, {
          schemeName: config.schemeName,
          uniqId: payload.data.uniqId,
        })
      yield put(setters.setListAddFirstBoard(objectWithPerms.data))
    },

    postStageSortStart: {
      effect: 'every',
      saga: function* ({ payload }) {
        yield call(stageSortService.postStageSortStart, payload)
      },
    },
    postStageSortEnd: {
      effect: 'every',
      saga: function* ({ payload }) {
        yield call(stageSortService.postStageSortEnd, payload)
      },
    },
    postStageSortBefore: {
      effect: 'every',
      saga: function* ({ payload }) {
        yield call(stageSortService.postStageSortBefore, payload)
      },
    },
    postStageSortAfter: {
      effect: 'every',
      saga: function* ({ payload }) {
        yield call(stageSortService.postStageSortAfter, payload)
      },
    },

    syncTableColumns: {
      effect: 'latest',
      saga: function* () {
        const smartListItem: state.IStateData['smartList']['item'] =
          yield select(selectors.smartListItem)

        const smartListItemDefaultUserOptions: state.IStateData['smartList']['itemDefaultUserOptions'] =
          yield select(selectors.smartListItemDefaultUserOptions)

        const userOptions =
          smartListItem?.userOptions || smartListItemDefaultUserOptions

        if (userOptions) {
          const fieldsGroups: state.IStateData['fieldsGroups']['list'] =
            yield select(selectors.fieldsGroupsList)

          const schemes: AppStoreState['schemas']['items'] = yield select(
            appStore.selectors.selectors.schemas
          )

          const fieldsGroupsColumns = storageObjectsHelpers.setupColumns({
            schemaName: config.schemeName,
            fieldsGroups: fieldsGroups.items,
            schemas: schemes,
            columns: userOptions.columns,
          })

          const fieldsGroupsBoard = storageObjectsHelpers.setupBoard({
            schemaName: config.schemeName,
            fieldsGroups: fieldsGroups.items,
            schemas: schemes,
            boardConfig: userOptions?.board,
          })

          yield put(setters.setBoardConfig(fieldsGroupsBoard))
          yield put(setters.setTableColumns(fieldsGroupsColumns))

          yield put(actions.cacheStorage.pending())
        }
      },
    },
    saveTableColumns: {
      effect: 'latest',
      saga: function* () {
        const smartListItem: state.IStateData['smartList']['item'] =
          yield select(selectors.smartListItem)

        const smartListItemDefault: state.IStateData['smartList']['item'] =
          yield select(selectors.smartListItemDefault)

        const columns: state.IStateData['table']['columns'] = yield select(
          selectors.tableColumns
        )
        const board: state.IStateData['board']['config'] = yield select(
          selectors.boardConfig
        )

        const userOptions = {
          columns: columns.map((column) => ({
            identifier: column.identifier,
            enable: column.enable,
            width: column.width,
            index: column.priority.index,
          })),

          board,
        }
        if (smartListItem) {
          yield call(smartListService.patchSmartListUserOptions, {
            schemeName: config.schemeName,
            uniqId: smartListItem.id,
            userOptions: userOptions,
          })
          yield put(
            setters.setSmartListUpdateUserOptions({
              id: smartListItem.id,
              userOptions,
            })
          )
        } else {
          yield call(smartListService.patchSmartListDefaultUserOptions, {
            schemeName: config.schemeName,
            userOptions: userOptions,
          })
          yield put(setters.setSmartListItemDefaultUserOptions(userOptions))
        }

        yield put(actions.syncTableColumns.pending())
      },
    },
    getSmartList: function* ({ payload }) {
      const smartList: IResponse<smartListService.IGetSmartListData> =
        yield call(smartListService.getSmartList, {
          schemeName: config.schemeName,
          ...payload,
        })

      if (payload.cursor) {
        yield put(
          setters.setSmartListsAppend({
            items: smartList.data,
            nextCursor: smartList.meta.nextCursor,
          })
        )
      } else {
        yield put(
          setters.setSmartLists({
            items: smartList.data,
            nextCursor: smartList.meta.nextCursor,
          })
        )
      }
    },

    getSmartListItem: {
      pending: function* ({ payload }) {
        const smartListItem: IResponse<smartListService.IGetSmartListItemData> =
          yield call(smartListService.getSmartListItem, {
            schemeName: config.schemeName,
            ...payload,
          })

        yield put(setters.setSmartListItem(smartListItem.data))
        yield put(actions.syncTableColumns.pending())
      },
      clear: function* () {
        yield put(setters.setSmartListItem(undefined))
        yield put(actions.syncTableColumns.pending())
      },
    },
    getSmartListItemDefault: {
      pending: function* ({ payload }) {
        try {
          const smartListItem: IResponse<smartListService.IGetSmartListItemData> =
            yield call(smartListService.getSmartListItemDefault, {
              schemeName: config.schemeName,
              ...payload,
            })

          yield put(setters.setSmartListItemDefault(smartListItem.data))
        } catch (error) {
          const smartListItem: IResponse<smartListService.IGetSmartListItemData> =
            yield call(smartListService.postSmartListDefault, {
              schemeName: config.schemeName,
              data: {},
            })

          yield put(setters.setSmartListItemDefault(smartListItem.data))
        }
        yield put(actions.getSmartListDefaultUserOptions.pending({}))
        yield put(actions.syncTableColumns.pending())
      },
      clear: function* () {
        yield put(setters.setSmartListItemDefault(undefined))
        yield put(actions.syncTableColumns.pending())
      },
    },
    setSmartListItem: {
      pending: function* ({ payload }) {
        yield put(setters.setSmartListItem(payload))
        // yield put(
        //   actions.getSmartListUserOptions.pending({
        //     uniqId: payload.id,
        //   })
        // )
        // yield put(actions.syncTableColumns.pending())
      },
      clear: function* () {
        yield put(setters.setSmartListItem(undefined))
        // yield put(actions.syncTableColumns.pending())
      },
    },
    setSmartListItemDefault: {
      pending: function* ({ payload }) {
        yield put(setters.setSmartListItemDefault(payload))
        // yield put(actions.getSmartListDefaultUserOptions.pending({}))
        // yield put(actions.syncTableColumns.pending())
      },
      clear: function* () {
        yield put(setters.setSmartListItemDefault(undefined))
        // yield put(actions.syncTableColumns.pending())
      },
    },
    putSmartList: function* ({ payload }) {
      const smartList: IResponse<smartListService.IPutSmartListData> =
        yield call(smartListService.putSmartList, {
          schemeName: config.schemeName,
          ...payload,
        })
      yield put(setters.setSmartListUpdate(smartList.data))

      yield put(
        appStore.actions.navigate({
          search: {
            smartListSelectedId: smartList.data.id,
          },
        })
      )
      yield put(setters.setSmartListUpdate(smartList.data))
    },

    postSmartList: function* ({ payload }) {
      console.debug({ 'MANUAL LIST': payload })

      if (payload.data.type === 'manual') {
        yield put(actions.postManualList.pending(payload))
      } else {
        const smartList: IResponse<smartListService.IPutSmartListData> =
          yield call(smartListService.postSmartList, {
            schemeName: config.schemeName,
            ...payload,
          })

        yield put(setters.setSmartListItem(smartList.data))
        yield put(setters.setSmartListAdd(smartList.data))

        // Subscribe to recalculation events immediately after creation
        subscribeToSmartListRecalculation()

        yield put(
          appStore.actions.navigate({
            search: {
              smartListSelectedId: smartList.data.id,
            },
          })
        )
      }
      yield put(
        app.actions.smartListsCountUp.pending({
          schemeName: config.schemeName,
          number: 1,
        })
      )
    },
    postManualList: function* ({ payload }) {
      console.debug('postManualList', payload)
      const smartList: IResponse<smartListService.IPutSmartListData> =
        yield call(smartListService.postSmartList, {
          schemeName: config.schemeName,
          ...payload,
        })

      yield put(setters.setSmartListItem(smartList.data))
      yield put(setters.setSmartListAdd(smartList.data))

      // Subscribe to recalculation events for manual list too
      subscribeToSmartListRecalculation()

      if (payload.data.objectIds?.length) {
        yield call(smartListService.postManualListObjectsAssign, {
          schemeName: config.schemeName,
          uniqId: smartList.data.id,
          objectsUniqIds: payload.data.objectIds,
        })
      }
      yield put(
        appStore.actions.navigate({
          search: {
            smartListSelectedId: smartList.data.id,
          },
        })
      )
    },

    deleteSmartList: function* ({ payload }) {
      const smartList: IResponse<smartListService.IPutSmartListData> =
        yield call(smartListService.deleteSmartList, {
          schemeName: config.schemeName,
          ...payload,
        })
      yield put(setters.setSmartListItem(undefined))
      yield put(
        appStore.actions.navigate({
          search: {
            smartListSelectedId: undefined,
          },
        })
      )

      yield put(setters.setSmartListRemove(payload))
      yield put(
        app.actions.smartListsCountUp.pending({
          schemeName: config.schemeName,
          number: -1,
        })
      )
    },

    postManualListObjectsAssign: function* ({ payload }) {
      yield call(smartListService.postManualListObjectsAssign, {
        schemeName: config.schemeName,
        ...payload,
      })
    },
    postManualListObjectsRemove: function* ({ payload }) {
      yield call(smartListService.postManualListObjectsRemove, {
        schemeName: config.schemeName,
        ...payload,
      })
      yield put(
        setters.setObjectsUniqIdsRemove({
          objectsUniqIds: payload.objectsUniqIds,
        })
      )
    },

    addSmartListUsers: function* ({ payload }) {
      const smartList: IResponse<smartListService.ISmartList> = yield call(
        smartListService.addSmartListUsers,
        {
          schemeName: config.schemeName,
          ...payload,
        }
      )
      yield put(setters.setSmartListUpdate(smartList.data))
    },
    removeSmartListUsers: function* ({ payload }) {
      const smartList: IResponse<smartListService.ISmartList> = yield call(
        smartListService.removeSmartListUsers,
        {
          schemeName: config.schemeName,
          ...payload,
        }
      )
      yield put(setters.setSmartListUpdate(smartList.data))
    },
    addSmartListGroups: function* ({ payload }) {
      const smartList: IResponse<smartListService.ISmartList> = yield call(
        smartListService.addSmartListGroups,
        {
          schemeName: config.schemeName,
          ...payload,
        }
      )
      yield put(setters.setSmartListUpdate(smartList.data))
    },
    removeSmartListGroups: function* ({ payload }) {
      const smartList: IResponse<smartListService.ISmartList> = yield call(
        smartListService.removeSmartListGroups,
        {
          schemeName: config.schemeName,
          ...payload,
        }
      )
      yield put(setters.setSmartListUpdate(smartList.data))
    },

    getSmartListDefaultUserOptions: function* ({ payload }) {
      const smartList: IResponse<smartListService.IGetSmartListDefaultUserOptionsData> =
        yield call(smartListService.getSmartListDefaultUserOptions, {
          schemeName: config.schemeName,
          ...payload,
        })

      yield put(setters.setSmartListItemDefaultUserOptions(smartList.data))
      yield put(actions.syncTableColumns.pending())
    },
    patchSmartListDefaultUserOptions: function* ({ payload }) {
      const smartList: IResponse<smartListService.IPatchSmartListDefaultUserOptionsData> =
        yield call(smartListService.patchSmartListDefaultUserOptions, {
          schemeName: config.schemeName,
          ...payload,
        })
      yield put(setters.setSmartListItemDefaultUserOptions(payload.userOptions))
      yield put(actions.syncTableColumns.pending())
    },
    getSmartListUserOptions: function* ({ payload }) {
      const smartListUserOptions: IResponse<smartListService.IGetSmartListUserOptionsData> =
        yield call(smartListService.getSmartListUserOptions, {
          schemeName: config.schemeName,
          ...payload,
        })

      yield put(
        setters.setSmartListUpdateUserOptions({
          id: payload.uniqId,
          userOptions: smartListUserOptions.data,
        })
      )
      yield put(actions.syncTableColumns.pending())
    },
    patchSmartListUserOptions: function* ({ payload }) {
      const smartListUserOptions: IResponse<smartListService.IPatchSmartListUserOptionsData> =
        yield call(smartListService.patchSmartListUserOptions, {
          schemeName: config.schemeName,
          ...payload,
        })
      yield put(
        setters.setSmartListUpdateUserOptions({
          id: payload.uniqId,
          userOptions: payload.userOptions,
        })
      )
      yield put(actions.syncTableColumns.pending())
    },

    getSmartListPermissions: function* ({ payload }) {
      const smartList: IResponse<smartListService.IGetSmartListPermissionsData> =
        yield call(smartListService.getSmartListPermissions, {
          schemeName: config.schemeName,
          ...payload,
        })
      yield put(setters.setSmartListPermissions(smartList.data))
    },

    postSmartListPermissions: function* ({ payload }) {
      const smartList: IResponse<smartListService.IPostSmartListPermissionsData> =
        yield call(smartListService.postSmartListPermissions, {
          schemeName: config.schemeName,
          ...payload,
        })
      // yield put(setters.setSmartListPermissions(smartList.data))
    },
    postSmartListPermissionsGroupsAdd: function* ({ payload }) {
      const smartList: IResponse<smartListService.ISmartList> = yield call(
        smartListService.postSmartListPermissionsGroupsAdd,
        {
          schemeName: config.schemeName,
          ...payload,
        }
      )
      yield put(setters.setSmartListUpdate(smartList.data))
    },
    postSmartListPermissionsGroupsRemove: function* ({ payload }) {
      const smartList: IResponse<smartListService.ISmartList> = yield call(
        smartListService.postSmartListPermissionsGroupsRemove,
        {
          schemeName: config.schemeName,
          ...payload,
        }
      )
      yield put(setters.setSmartListUpdate(smartList.data))
    },
    postSmartListPermissionsUsersAdd: function* ({ payload }) {
      const smartList: IResponse<smartListService.ISmartList> = yield call(
        smartListService.postSmartListPermissionsUsersAdd,
        {
          schemeName: config.schemeName,
          ...payload,
        }
      )
      yield put(setters.setSmartListUpdate(smartList.data))
    },
    postSmartListPermissionsUsersRemove: function* ({ payload }) {
      const smartList: IResponse<smartListService.ISmartList> = yield call(
        smartListService.postSmartListPermissionsUsersRemove,
        {
          schemeName: config.schemeName,
          ...payload,
        }
      )
      yield put(setters.setSmartListUpdate(smartList.data))
    },
    importSettingsUpdate: function* ({ payload }) {
      yield put(setters.setImportSettingsUpdate(payload))
      // yield put(actions.cacheStorage.pending())
    },
    logging: {
      effect: (pattern, saga) => takeLatest('*', saga),
      controlled: true,
      saga: function* (data) {
        if (
          data.type.includes(config.prefix) &&
          !data.type.includes('cacheStorage')
        ) {
          yield put(actions.cacheStorage.pending())
        }
      },
    },
    cacheStorage: {
      effect: 'latest',
      controlled: true,
      saga: function* (data) {
        if (data.type.includes(config.prefix)) {
          yield delay(1000)
          try {
            // console.debug('cacheStorage')
            const stateData: state.IStateData = yield select(selectors.data)

            localStorage.setItem(
              config.prefix,
              JSON.stringify({
                ...stateData,

                objects: {
                  ...stateData.objects,
                  deleteValidation: null,
                  item: undefined,
                  list: {
                    items: [], //stateData.objects.list.items.slice(0,25)
                    nextCursor: null,
                    previousCursor: null,
                  },
                },

                smartList: {
                  ...stateData.smartList,
                  list: {
                    items: stateData.smartList.list.items.slice(0, 5),
                  },
                },
                table: {
                  ...stateData.table,
                  filters: {},
                },
              } as state.IStateData)
            )
          } catch (e) {
            console.debug(e)
          }
        }
      },
    },
  })
}
